"use client";

import { useLocale } from "@/lib/hooks/use-locale";
import { formatLocation, formatProvince, formatOfficeLocations } from "@/lib/provinces";
import { langCodesToDisplay } from "@/lib/lang-utils";
import type { Locale } from "@/lib/locale";

// Thin client wrappers that re-render when the user toggles the EN/VI flag,
// so server-rendered surfaces (homepage cards, profile page header, office
// address) flip *without a page refresh*. The server-side render passes
// `initialLocale` (from `getServerLocale()`) so the first client render
// matches the SSR output — no hydration mismatch — and the `useLocale()`
// hook takes over after mount.
//
// Why three components instead of one generic `<LocaleText render={…}>`:
// React Server Components can't pass functions across the server→client
// boundary, so the prop shape has to be plain serialisable data.

interface LocationLabelProps {
  /** Raw `professionals.location` slug. */
  slug: string | null | undefined;
  /** Locale read by the server from the cookie, for hydration parity. */
  initialLocale: Locale;
  /** Suffix appended after the city, e.g. ", Vietnam". Empty by default. */
  suffix?: string;
  /** Fallback when slug is empty / unknown. Empty by default. */
  fallback?: string;
}

/**
 * Locale-aware location label that flips live on EN/VI toggle.
 * Use on server-rendered surfaces (profile page header, ProfessionalCard).
 * For client-only consumers that already have a `useLocale()` call upstream,
 * keep using `formatLocation(slug, locale)` directly — wrapping in this
 * component would only add a re-render layer.
 */
export function LocationLabel({
  slug,
  initialLocale,
  suffix = "",
  fallback = "",
}: LocationLabelProps) {
  const locale = useLocale(initialLocale);
  if (!slug) return <>{fallback}</>;
  const label = formatLocation(slug, locale);
  if (!label) return <>{fallback}</>;
  return <>{label}{suffix}</>;
}

/**
 * Locale-aware MULTI-location label — every province a pro has an office in,
 * pre-ordered by the caller (main office first; `ProfessionalFull.offices`
 * already carries that order), joined with " · ". Falls back to the plain
 * single-location render for the common one-office case.
 * "Ho Chi Minh City · Ha Noi · Da Nang, Vietnam" ↔ "TP. Hồ Chí Minh · Hà Nội ·
 * Đà Nẵng, Vietnam".
 */
export function LocationsLabel({
  slugs,
  initialLocale,
  suffix = "",
  fallback = "",
}: {
  /** Province slugs, main office first. Duplicates are dropped. */
  slugs: (string | null | undefined)[];
  initialLocale: Locale;
  suffix?: string;
  fallback?: string;
}) {
  const locale = useLocale(initialLocale);
  const label = formatOfficeLocations(slugs, locale);
  if (!label) return <>{fallback}</>;
  return <>{label}{suffix}</>;
}

/**
 * Locale-aware province-only label. Same shape as `LocationLabel` but
 * routes through `formatProvince` so the `remote` sentinel never
 * accidentally renders — use on physical-address surfaces.
 */
export function ProvinceLabel({
  slug,
  initialLocale,
  fallback = "",
}: {
  slug: string | null | undefined;
  initialLocale: Locale;
  fallback?: string;
}) {
  const locale = useLocale(initialLocale);
  if (!slug) return <>{fallback}</>;
  const label = formatProvince(slug, locale);
  if (!label) return <>{fallback}</>;
  return <>{label}</>;
}

/**
 * Locale-aware comma-joined language list ("English, Vietnamese" ↔
 * "Tiếng Anh, Tiếng Việt"). Re-renders on toggle.
 */
export function LanguagesLabel({
  codes,
  initialLocale,
  fallback = "",
}: {
  codes: readonly string[] | null | undefined;
  initialLocale: Locale;
  fallback?: string;
}) {
  const locale = useLocale(initialLocale);
  if (!codes || codes.length === 0) return <>{fallback}</>;
  return <>{langCodesToDisplay([...codes], locale)}</>;
}
